import styled, { css, keyframes } from "styled-components";
import {
  SPACING,
  SPACING_RESPONSIVE,
  QUERIES,
  ANIMATION,
} from "../../../utils/constants";
import {
  StyledWrapperProps,
  StyledValueProps,
  StyledIconContainerProps,
} from "./types";

// Keyframes for animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-8px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

// Main wrapper component
export const Wrapper = styled.div<StyledWrapperProps>`
  position: relative;
  padding: ${SPACING_RESPONSIVE.mobile.cardPadding}px;
  border-radius: 16px;
  border: 1px solid var(--color-card-border);
  transition:
    transform ${ANIMATION.duration.normal} ${ANIMATION.easing.easeOut},
    box-shadow ${ANIMATION.duration.normal} ${ANIMATION.easing.easeOut},
    border-color ${ANIMATION.duration.normal} ${ANIMATION.easing.easeOut};
  background: var(--color-card-background);
  overflow: hidden;
  animation: ${fadeIn} ${ANIMATION.duration.slower} ${ANIMATION.easing.easeOut};

  /* Clickable state */
  ${(props) =>
    props.$clickable &&
    css`
      cursor: pointer;

      &:hover {
        transform: translateY(-4px);
        box-shadow: var(--shadow-large-2);
        border-color: ${props.color};
      }

      &:active {
        transform: translateY(-2px);
        box-shadow: var(--shadow-large-3);
        transition: transform ${ANIMATION.duration.instant}
          ${ANIMATION.easing.easeOut};
      }

      /* Focus visible for accessibility */
      &:focus-visible {
        outline: 2px solid ${props.color};
        outline-offset: 2px;
      }
    `}

  /* Non-clickable state */
  ${(props) =>
    !props.$clickable &&
    css`
      &:hover {
        transform: translateY(-2px);
        box-shadow: var(--shadow-large-3);
      }
    `}

  /* Background decoration */
  &::before {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    width: 100px;
    height: 100px;
    background: linear-gradient(
      135deg,
      ${(props) => props.color}10,
      ${(props) => props.color}05
    );
    border-radius: 50%;
    transform: translate(40px, -40px);
    transition: ${ANIMATION.transitions.allSlow};
  }

  &:hover::before {
    transform: translate(30px, -30px) scale(1.1);
  }

  @media ${QUERIES.tabletAndUp} {
    padding: ${SPACING_RESPONSIVE.tablet.cardPadding}px;
  }

  @media ${QUERIES.laptopAndUp} {
    padding: ${SPACING_RESPONSIVE.desktop.cardPadding}px;
  }
`;

// Header section with icon and name
export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${SPACING.md}px;
  position: relative;
  z-index: 2;
`;

export const NameSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm + SPACING.xs}px;
  flex: 1;
`;

export const IconContainer = styled.div<StyledIconContainerProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${(props) => `${props.color}15`};
  color: ${(props) => props.color};
  transition: ${ANIMATION.transitions.allSlow};

  svg {
    width: 24px;
    height: 24px;
    stroke-width: 2;
  }

  ${Wrapper}:hover & {
    transform: scale(1.1) rotate(5deg);
    background: ${(props) => `${props.color}25`};
  }

  @media not all and ${QUERIES.tabletAndUp} {
    width: 36px;
    height: 36px;

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

export const Name = styled.h3`
  font-size: var(--text-md);
  font-weight: 600;
  color: var(--color-text-default);
  line-height: 1.4;
  margin: 0;
  transition: color ${ANIMATION.duration.moderate} ${ANIMATION.easing.easeOut};

  @media not all and ${QUERIES.tabletAndUp} {
    font-size: var(--text-md);
  }
`;

// Navigation arrow indicator
export const NavigationArrow = styled.span<{ color: string }>`
  font-size: var(--text-xl);
  color: ${(props) => props.color};
  opacity: 0;
  transform: translateX(-8px);
  transition: ${ANIMATION.transitions.allSlow};

  ${Wrapper}:hover & {
    opacity: 1;
    transform: translateX(0);
    animation: ${slideIn} ${ANIMATION.duration.moderate}
      ${ANIMATION.easing.easeOut};
  }

  @media not all and ${QUERIES.tabletAndUp} {
    font-size: var(--text-lg);
  }
`;

// Value section
export const ValueSection = styled.div`
  margin-bottom: ${SPACING.sm}px;
  position: relative;
  z-index: 2;
`;

export const Value = styled.div<StyledValueProps>`
  font-size: var(--text-xxxl);
  font-weight: 700;
  line-height: 1.2;
  color: ${(props) => props.color};
  transition: ${ANIMATION.transitions.allSlow};
  margin: 0;

  ${Wrapper}:hover & {
    transform: scale(1.03);
  }

  @media not all and ${QUERIES.tabletAndUp} {
    font-size: var(--text-xxl);
  }
`;

// Loading skeleton styles
export const LoadingSkeleton = styled.div`
  background: var(--gradient-skeleton);
  background-size: 200px 100%;
  animation: ${shimmer} 2s infinite;
  border-radius: 8px;

  /* Performance optimizations for 60fps */
  transform: translateZ(0);
  will-change: background-position;
  contain: layout style paint;
`;

export const SkeletonHeader = styled(LoadingSkeleton)`
  height: 40px;
  margin-bottom: ${SPACING.md}px;
  width: 60%;
`;

export const SkeletonValue = styled(LoadingSkeleton)`
  height: 32px;
  width: 70%;
  margin-bottom: ${SPACING.sm + SPACING.xs}px;
`;

// Responsive utilities
export const ResponsiveContainer = styled.div`
  height: 100%;
  /* Removed padding: 8px 0px to eliminate triple-layer padding compound.
     Spacing is now handled by grid containers and card internal padding only. */

  @media (max-width: 640px) {
    ${IconContainer} {
      width: 36px;
      height: 36px;

      svg {
        width: 18px;
        height: 18px;
      }
    }
  }
`;

// Error state styles
export const ErrorState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${SPACING.md + SPACING.xs}px;
  color: var(--color-error);
  font-size: var(--text-base);
  text-align: center;
  animation: ${pulse} 2s infinite;
  min-height: 120px;
`;

export const ErrorHint = styled.div`
  font-size: var(--text-xs);
  margin-top: ${SPACING.xs}px;
  opacity: 0.7;
`;
